import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import TitleIcon from '@mui/icons-material/Title';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LinkIcon from '@mui/icons-material/Link';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export interface MarkdownFieldEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  minRows?: number;
  placeholder?: string;
}

/** WYSIWYG markdown editor (TipTap) — the surface itself shows formatted text, not raw markdown syntax. */
export function MarkdownFieldEditor({ label, value, onChange, minRows = 5, placeholder }: MarkdownFieldEditorProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // TipTap's editor instance isn't reactive to prop changes by itself — bump this to
  // re-render the toolbar's active-state highlighting on every selection/content change.
  const [, forceRerender] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown,
      Image,
      Placeholder.configure({ placeholder: placeholder ?? 'Write in Markdown…' }),
    ],
    content: value,
    contentType: 'markdown',
    onUpdate: ({ editor: updatedEditor }) => onChange(updatedEditor.getMarkdown()),
    onTransaction: () => forceRerender((n) => n + 1),
    editorProps: {
      attributes: { 'aria-label': label },
    },
  });

  // Sync external value changes (Randomize, loading a different draft, etc.) into the
  // editor. Skipped when the value already matches what the editor produced itself, so
  // this doesn't fight the user's own typing or reset cursor position mid-edit.
  useEffect(() => {
    if (!editor) return;
    if (editor.getMarkdown() === value) return;
    editor.commands.setContent(value, { contentType: 'markdown' });
  }, [editor, value]);

  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;
    const dataUrl = await readFileAsDataUrl(file);
    editor.chain().focus().setImage({ src: dataUrl, alt: file.name }).run();
    event.target.value = '';
  };

  const handleLink = () => {
    if (!editor) return;
    const previousUrl = (editor.getAttributes('link').href as string | undefined) ?? 'https://';
    const url = window.prompt('Link URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  if (!editor) return null;

  return (
    <Box>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }} className="no-print">
        <Tooltip title="Bold">
          <IconButton
            size="small"
            color={editor.isActive('bold') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton
            size="small"
            color={editor.isActive('italic') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Heading">
          <IconButton
            size="small"
            color={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            <TitleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bullet list">
          <IconButton
            size="small"
            color={editor.isActive('bulletList') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Link">
          <IconButton size="small" color={editor.isActive('link') ? 'primary' : 'default'} onClick={handleLink}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert image">
          <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
            <ImageOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleImageChange} />
      </Stack>

      <Box
        className="no-print"
        onClick={() => editor.chain().focus().run()}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 2,
          minHeight: minRows * 28,
          cursor: 'text',
          bgcolor: 'background.paper',
          '& .ProseMirror': { outline: 'none' },
          '& .ProseMirror p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
          '& p': { m: 0, mb: 1 },
          '& p:last-child': { mb: 0 },
          '& h3': { fontSize: '1.05rem', fontWeight: 700, mt: 1.5, mb: 0.5 },
          '& ul, & ol': { pl: 3, mb: 1 },
          '& a': { color: 'primary.main' },
          '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
